import { resizeFrontGeometryY } from './sectionGeometry/sectionFrontGeometryResizer';

// Resizes one rendered cabinet section slot and keeps child cap meshes in the
// same vertical proportions they had inside the original GLB section mesh.
const SECTION_CAP_TAG = 'tallCabinetSectionCap';
const SECTION_CAP_ORIG_POS_Y_TAG = 'tallCabinetSectionCapOrigPosY';
const SECTION_CAP_ORIG_SCALE_Y_TAG = 'tallCabinetSectionCapOrigScaleY';
const SECTION_CAP_ORIG_HEIGHT_TAG = 'tallCabinetSectionCapOrigHeight';
const SECTION_CAP_MIN_RATIO_TAG = 'tallCabinetSectionCapMinRatio';
const SECTION_CAP_MAX_RATIO_TAG = 'tallCabinetSectionCapMaxRatio';

export function buildBoundingBoxInParentSpace(mesh) {
  return buildBoundingBoxInObjectSpace(mesh, mesh.parent);
}

function buildBoundingBoxInObjectSpace(mesh, objectSpace) {
  const THREE = window.THREE;
  const box = new THREE.Box3().setFromObject(mesh);
  const objectWorld = new THREE.Matrix4();

  objectWorld.copy(objectSpace.matrixWorld).invert();
  box.applyMatrix4(objectWorld);

  return box;
}

function isSectionCapMesh(mesh) {
  return mesh.isMesh && /_Cap(?:\d+)?($|_|\.)/.test(mesh.name);
}

function getSectionCapMeshes(sectionMesh) {
  const capMeshes = [];

  sectionMesh.traverse((child) => {
    if (child !== sectionMesh && isSectionCapMesh(child)) {
      capMeshes.push(child);
    }
  });

  return capMeshes;
}

/**
 * Records cap positions as ratios inside the parent section. Caps often have
 * separate geometry/materials from the front mesh and must follow the section
 * after height edits instead of staying at their authored Y position.
 */
export function recordSectionCapMetrics(sectionMesh) {
  const capMeshes = getSectionCapMeshes(sectionMesh);
  const sectionHeight = sectionMesh.userData.origHeight;

  if (!sectionHeight || sectionHeight <= 0) {
    return;
  }

  capMeshes.forEach((capMesh) => {
    if (
      capMesh.userData[SECTION_CAP_TAG]
      && typeof capMesh.userData[SECTION_CAP_MIN_RATIO_TAG] === 'number'
      && typeof capMesh.userData[SECTION_CAP_MAX_RATIO_TAG] === 'number'
    ) {
      return;
    }

    const capBox = buildBoundingBoxInObjectSpace(capMesh, sectionMesh.parent);
    const capHeight = capBox.max.y - capBox.min.y;

    capMesh.userData[SECTION_CAP_TAG] = true;
    capMesh.userData[SECTION_CAP_ORIG_POS_Y_TAG] = capMesh.position.y;
    capMesh.userData[SECTION_CAP_ORIG_SCALE_Y_TAG] = capMesh.scale.y;
    capMesh.userData[SECTION_CAP_ORIG_HEIGHT_TAG] = capHeight;
    capMesh.userData[SECTION_CAP_MIN_RATIO_TAG] = (capBox.min.y - sectionMesh.userData.origMinY) / sectionHeight;
    capMesh.userData[SECTION_CAP_MAX_RATIO_TAG] = (capBox.max.y - sectionMesh.userData.origMinY) / sectionHeight;
  });
}

/**
 * Fits one logical cabinet section into a layout slot. Front meshes use a
 * vertical 9-slice deformation; simple meshes fall back to uniform Y scaling.
 */
export function applySlotToMeshes(meshes, slotBottomLocalY, slotHeightLocal) {
  if (!meshes.length) {
    return;
  }

  const groupMinY = Math.min(...meshes.map((mesh) => mesh.userData.origMinY));
  const groupMaxY = Math.max(...meshes.map((mesh) => mesh.userData.origMaxY));
  const groupHeight = groupMaxY - groupMinY;

  if (!groupHeight || groupHeight <= 0) {
    return;
  }

  meshes.forEach((mesh) => {
    const origHeight = mesh.userData.origHeight;
    if (!origHeight || origHeight <= 0) {
      return;
    }

    const origScaleY = mesh.userData.origScaleY || 1;
    const origPosY = mesh.userData.origPosY || 0;
    const offsetToBottom = mesh.userData.origMinY - origPosY;
    // A drawer can contain a front, box, handle, and caps as separate meshes.
    // Keep every part in its authored proportion inside the shared slot instead
    // of stretching each mesh independently to the full section height.
    const meshBottomRatio = (mesh.userData.origMinY - groupMinY) / groupHeight;
    const meshHeightRatio = origHeight / groupHeight;
    const meshSlotBottomLocalY = slotBottomLocalY + (slotHeightLocal * meshBottomRatio);
    const meshSlotHeightLocal = slotHeightLocal * meshHeightRatio;
    const targetHeightInMeshSpace = meshSlotHeightLocal / origScaleY;
    const isDeformed = resizeFrontGeometryY(mesh, targetHeightInMeshSpace);

    if (isDeformed) {
      mesh.scale.y = origScaleY;
      mesh.position.y = meshSlotBottomLocalY - offsetToBottom;
      applySectionCapsToDeformedSlot(mesh, meshSlotBottomLocalY, meshSlotHeightLocal);
      mesh.visible = true;
      return;
    }

    const scaleY = meshSlotHeightLocal / origHeight;

    mesh.scale.y = origScaleY * scaleY;
    mesh.position.y = meshSlotBottomLocalY - (offsetToBottom * scaleY);
    mesh.visible = true;
  });
}

function getLocalYAxisInObjectSpace(sourceObject, targetObject) {
  const THREE = window.THREE;
  const origin = new THREE.Vector3(0, 0, 0);
  const yAxis = new THREE.Vector3(0, 1, 0);

  sourceObject.localToWorld(origin);
  sourceObject.localToWorld(yAxis);
  targetObject.worldToLocal(origin);
  targetObject.worldToLocal(yAxis);

  return yAxis.y - origin.y;
}

function applySectionCapsToDeformedSlot(sectionMesh, slotBottomLocalY, slotHeightLocal) {
  const capMeshes = getSectionCapMeshes(sectionMesh);

  if (capMeshes.length === 0) {
    return;
  }

  sectionMesh.updateMatrixWorld(true);
  const sectionParent = sectionMesh.parent;

  capMeshes.forEach((capMesh) => {
    const origPosY = capMesh.userData[SECTION_CAP_ORIG_POS_Y_TAG];
    const origScaleY = capMesh.userData[SECTION_CAP_ORIG_SCALE_Y_TAG];
    const origHeight = capMesh.userData[SECTION_CAP_ORIG_HEIGHT_TAG];
    const minRatio = capMesh.userData[SECTION_CAP_MIN_RATIO_TAG];
    const maxRatio = capMesh.userData[SECTION_CAP_MAX_RATIO_TAG];

    if (
      typeof origPosY !== 'number'
      || typeof origScaleY !== 'number'
      || typeof origHeight !== 'number'
      || typeof minRatio !== 'number'
      || typeof maxRatio !== 'number'
      || origHeight <= 0
      || maxRatio <= minRatio
    ) {
      return;
    }

    const targetCapBottomY = slotBottomLocalY + (slotHeightLocal * minRatio);
    const targetCapHeight = slotHeightLocal * (maxRatio - minRatio);

    capMesh.position.y = origPosY;
    capMesh.scale.y = origScaleY * (targetCapHeight / origHeight);
    capMesh.updateMatrixWorld(true);

    const capBox = buildBoundingBoxInObjectSpace(capMesh, sectionParent);
    const deltaInSectionParent = targetCapBottomY - capBox.min.y;
    const parentUnitY = getLocalYAxisInObjectSpace(capMesh.parent, sectionParent);

    if (!parentUnitY) {
      return;
    }

    capMesh.position.y += deltaInSectionParent / parentUnitY;
    capMesh.updateMatrixWorld(true);
  });
}
